import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useChatContext } from 'src/components/Chat/Chat.hook';
import { withHumanAction } from 'src/components/Chat/Chat.humanAction';
import { ChatService } from 'src/components/Chat/Chat.service';
import { revealLeadAttributes } from 'src/components/Chat/components/ChatInfo/ChatInfo.functions';
import useContactBlock from 'src/components/Chat/useContactBlock.hook';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { RouterPaths } from 'src/components/Router/Router.constants';
import useOpenUtilityTemplateEditor from 'src/pages/WhatsappApi/hooks/useOpenUtilityTemplateEditor.hook';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import { eWhatsappApiWindow } from 'src/pages/WhatsappApi/WhatsappApi.enum';
import { useIntercomContext } from 'src/providers/IntercomProvider/useIntercomContext.hook';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
} from 'src/utils/ErrorDirectory';
import {
  META_ERROR_NOTICE_EXTERNAL_URLS,
  META_ERROR_NOTICE_NAMESPACE,
  isSupportAction,
} from 'src/utils/metaErrorNotice';
import {
  tMetaErrorNotice,
  tMetaErrorNoticeAction,
} from 'src/utils/metaErrorNotice.types';

import {
  hasApprovedUtilityTemplate,
  retryRefusalCopyKey,
} from './MetaErrorNotice.functions';
import { tMetaErrorNoticeButton } from './MetaErrorNotice.types';

// Notices whose support message already went out in this page session, by
// chat event. D17: each click opens a conversation, so a notice sends once,
// also when its bubble mounts again (chat reopened, scrolled back).
const supportSentEvents = new Set<string>();

/**
 * RET-2339 (D6): the kinds that send to the lead or open a surface that does
 * (the template picker, the template editor). A WhatsApp API chat whose plan
 * has no WhatsApp API offers none of them: its composer is the read-only
 * legend. Navigation, support, unblock and lead-edit kinds stay.
 */
const SEND_SHAPED_KINDS: ReadonlySet<tMetaErrorNoticeAction['kind']> = new Set<
  tMetaErrorNoticeAction['kind']
>([
  'retry',
  'sendTemplate',
  'chooseOtherTemplate',
  'editMessage',
  'chooseUtilityTemplate',
]);

/**
 * INT-1504: what the notice's button does for a catalog action.
 *
 * - sendTemplate / chooseOtherTemplate / editMessage: open the composer's
 *   template picker. The failed event does not record which template it was,
 *   so "Editar mensaje" cannot open that template's variables directly yet
 *   (INT-1517).
 * - chooseUtilityTemplate: the same picker, filtered to Seguimiento, Meta's
 *   UTILITY (131049). D5: only while the workspace has an approved UTILITY
 *   template; otherwise "Crear plantilla de seguimiento" opens the template
 *   editor on UTILITY. The catalog is the one the composer already loads
 *   (WhatsappApi context); until it has, the notice offers neither.
 * - viewNumberQuality / viewAccountStatus / reviewConnection: WhatsApp
 *   Business API → General, inside Leadsales, where the number's quality, the
 *   account's status and the connection live.
 * - unblockLead: the chat's own "Desbloquear usuario" (INT-1294).
 * - viewCredits (INT-1500 U7): WhatsApp Business API → Créditos, which shows
 *   the wallet cohort its balance instead.
 * - editLead (INT-1500 U7): the lead's attributes beside the chat, where the
 *   fields a template fills in are edited.
 * - contactSupport / talkToSupport (131060, 2011): sends support "Aviso de WhatsApp en el chat, código
 *   {code}: {reason}" in one click (INT-1500 D15, D17), without the code when
 *   the notice shows none (2011, BUG-1141), then reads "Enviado a soporte" and
 *   stays disabled. Until Intercom knows the user (JWT) it only
 *   pre-fills the messenger, so nothing goes out as an anonymous visitor.
 * - retry: messaging's BUG-908 retry through bff-web. The bubble updates from
 *   the socket; a refusal becomes a toast.
 * - external: a link to Meta, opened in a new tab.
 *
 * RET-2339 (D6): in a WhatsApp API chat whose plan has no WhatsApp API, the
 * send-shaped kinds (`SEND_SHAPED_KINDS`) offer no button. The check is the
 * shared plan guard's (`useWapiPlanSendGuard`, RET-2349), on the RESOLVED
 * channel (`selectedChat.integration`), like `reSendMessage`.
 */
export default function useMetaErrorNoticeAction(
  notice: tMetaErrorNotice,
  eventId: string,
): tMetaErrorNoticeButton | undefined {
  const { t } = useTranslation([META_ERROR_NOTICE_NAMESPACE, 'errors/errors']);
  const navigate = useNavigate();
  const { selectedChat, setTemplatePickerRequest } = useChatContext();
  const { setWhatsappApiWindow, templates, templatesHydrated } =
    useWhatsappApiContext();
  const { setChatModalIsOpen } = useModalsContext();
  const openUtilityTemplateEditor = useOpenUtilityTemplateEditor();
  const { methods: intercomMethods, isIdentified } = useIntercomContext();
  const { addAlert } = useToastContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { loading: isUnblocking, setContactBlocked } = useContactBlock();
  const { isWapiPlanSendBlocked } = useWapiPlanSendGuard();
  const [isRetrying, setIsRetrying] = useState(false);
  const [sentToSupport, setSentToSupport] = useState(() =>
    supportSentEvents.has(eventId),
  );

  // Render decision: the silent variant. The notice hides the button
  // instead of toasting (RET-2349, KTD2).
  const isSendWithoutWapiPlanBlocked = isWapiPlanSendBlocked(
    selectedChat?.integration,
  );

  const { action, actionKey } = notice;
  if (!action || !actionKey) return undefined;

  if (isSendWithoutWapiPlanBlocked && SEND_SHAPED_KINDS.has(action.kind)) {
    return undefined;
  }

  // D5: before the catalog loads, "no approved template" only means "not
  // loaded yet", and neither label would be true.
  if (action.kind === 'chooseUtilityTemplate' && !templatesHydrated) {
    return undefined;
  }
  const createsUtilityTemplate =
    action.kind === 'chooseUtilityTemplate' &&
    !hasApprovedUtilityTemplate(templates);

  const isSentToSupport = isSupportAction(action.kind) && sentToSupport;
  const label = t(
    (createsUtilityTemplate && 'actions.createUtilityTemplate') ||
      (isSentToSupport && 'actions.sentToSupport') ||
      actionKey,
    { ns: META_ERROR_NOTICE_NAMESPACE },
  );

  if (action.kind === 'external') {
    return {
      type: 'external',
      label,
      href: META_ERROR_NOTICE_EXTERNAL_URLS[action.target],
    };
  }

  async function retry() {
    if (isRetrying) return;
    // Defense in depth: the button is not offered in this case (above).
    if (isSendWithoutWapiPlanBlocked) return;
    if (!currentWorkspaceId || !selectedChat?.id) {
      addAlert({
        type: eAlertType.WARNING,
        message: t('retry.failed', { ns: META_ERROR_NOTICE_NAMESPACE }),
      });
      return;
    }
    setIsRetrying(true);
    try {
      const chatId = selectedChat.id;
      // INT-1538 U10 (KTD11): a retry is a person acting; recorded first.
      await withHumanAction(currentWorkspaceId, selectedChat, () =>
        ChatService.retryWapiMessage(currentWorkspaceId, chatId, eventId),
      );
      addAlert({
        type: eAlertType.INFO,
        message: t('retry.started', { ns: META_ERROR_NOTICE_NAMESPACE }),
      });
    } catch (error) {
      const pause = lookupOutboundRefusal(error);
      addAlert(
        pause
          ? buildOutboundRefusalAlert(pause, t)
          : {
              type: eAlertType.WARNING,
              message: t(retryRefusalCopyKey(error), {
                ns: META_ERROR_NOTICE_NAMESPACE,
              }),
            },
      );
    } finally {
      setIsRetrying(false);
    }
  }

  function openWhatsappBusinessWindow(target: eWhatsappApiWindow) {
    setWhatsappApiWindow(target);
    setChatModalIsOpen(false);
    navigate(RouterPaths.wapi);
  }

  const openWhatsappBusinessGeneral = () =>
    openWhatsappBusinessWindow(eWhatsappApiWindow.GENERAL);

  function contactSupport() {
    if (sentToSupport) return;
    // BUG-1141: a notice that shows no code (2011) sends none either,
    // instead of its catalog key after "código".
    const message = t(
      notice.displayCode ? 'support.message' : 'support.messageWithoutCode',
      {
        ns: META_ERROR_NOTICE_NAMESPACE,
        code: notice.displayCode,
        reason: notice.supportReasonKey
          ? t(notice.supportReasonKey, { ns: META_ERROR_NOTICE_NAMESPACE })
          : '',
      },
    );
    if (!isIdentified) {
      intercomMethods.showNewMessage(message);
      return;
    }
    intercomMethods.startConversation(message);
    supportSentEvents.add(eventId);
    setSentToSupport(true);
  }

  const openTemplatePicker = () =>
    setTemplatePickerRequest({ templateType: 'ALL' });

  const onClickByKind: Record<
    Exclude<tMetaErrorNoticeAction['kind'], 'external'>,
    () => void
  > = {
    // INT-1604 (D4): opening the picker is not dealing with the failure —
    // the template the person then sends is, and that send records it.
    sendTemplate: openTemplatePicker,
    chooseOtherTemplate: openTemplatePicker,
    editMessage: openTemplatePicker,
    chooseUtilityTemplate: () => {
      if (createsUtilityTemplate) openUtilityTemplateEditor();
      else setTemplatePickerRequest({ templateType: 'UTILITY' });
    },
    viewNumberQuality: openWhatsappBusinessGeneral,
    viewAccountStatus: openWhatsappBusinessGeneral,
    reviewConnection: openWhatsappBusinessGeneral,
    unblockLead: () => {
      void setContactBlocked(false);
    },
    viewCredits: () => openWhatsappBusinessWindow(eWhatsappApiWindow.CREDITS),
    editLead: () => revealLeadAttributes(),
    contactSupport,
    // D15: 131060's "Hablar con soporte" goes the same way, with its reason.
    talkToSupport: contactSupport,
    retry: () => {
      void retry();
    },
  };

  return {
    type: 'internal',
    label,
    onClick: onClickByKind[action.kind],
    disabled:
      (action.kind === 'retry' && isRetrying) ||
      (action.kind === 'unblockLead' && isUnblocking) ||
      isSentToSupport,
  };
}
